// ImageAlbumPlugin.ts
// Minimal CKEditor 5 plugin — opens the React ImageAlbumModal via Zustand store

import { createDropdown, Plugin } from "ckeditor5"
import { useAlbumModalStore } from "@/stores/use-album-modal-store"

export class ImageAlbumPlugin extends Plugin {
  static get pluginName() {
    return "ImageAlbumPlugin" as const
  }

  init() {
    const editor = this.editor

    editor.ui.componentFactory.add("insertImageAlbum", () => {
      const dropdownView = createDropdown(editor.locale)

      dropdownView.buttonView.set({
        label: "Album ảnh",
        tooltip: true,
        withText: true,
        icon: false,
      })

      const layouts = [
        { id: "grid", label: "Lưới (Grid)", icon: "▦" },
        { id: "masonry", label: "Xếp gạch (Masonry)", icon: "⊟" },
        { id: "slider", label: "Trình chiếu (Slider)", icon: "◁▷" },
      ] as const

      dropdownView.on("change:isOpen", () => {
        if (dropdownView.panelView.element && dropdownView.panelView.element.childNodes.length > 0) return

        const container = document.createElement("div")
        container.style.cssText = "padding:8px;min-width:220px;font-family:inherit;"

        layouts.forEach(layout => {
          const btn = document.createElement("button")
          btn.type = "button"
          btn.style.cssText = `
            display:flex;align-items:center;gap:10px;
            width:100%;padding:8px 12px;
            background:transparent;border:1px solid transparent;
            border-radius:6px;cursor:pointer;font-size:13px;
            color:#1a1a1a;text-align:left;
            transition:background 0.12s;
          `
          btn.innerHTML = `<span style="font-size:16px;width:24px;text-align:center;">${layout.icon}</span><span>${layout.label}</span>`

          btn.addEventListener("mouseenter", () => { btn.style.background = "#f0f4ff" })
          btn.addEventListener("mouseleave", () => { btn.style.background = "transparent" })

          btn.addEventListener("click", () => {
            dropdownView.isOpen = false
            window.dispatchEvent(new CustomEvent("ck-open-album-modal", {
              detail: { layout: layout.id },
            }))
          })

          container.appendChild(btn)
        })

        dropdownView.panelView.element!.appendChild(container)
      })

      return dropdownView
    })
  }
}

// ─── Bridge ────────────────────────────────────────────────────────────────────

let bridgeRegistered = false

export function registerAlbumBridge() {
  if (bridgeRegistered || typeof window === "undefined") return
  bridgeRegistered = true

  window.addEventListener("ck-open-album-modal", ((e: Event) => {
    const detail = (e as CustomEvent).detail as {
      layout?: "grid" | "masonry" | "slider"
      editingAlbum?: HTMLElement | { element: HTMLElement | null; images: Array<{ url: string; display?: string; thumbnail?: string; alt: string; name?: string }>; layout: "grid" | "masonry" | "slider" }
    }
    const { layout = "grid", editingAlbum } = detail

    // editorRef is stored on the window so the plugin can find it
    const editorRef = (window as any).__ckeditorAlbumEditorRef

    // If editingAlbum is an HTMLElement, parse it
    if (editingAlbum && !(editingAlbum instanceof HTMLElement)) {
      // It's already parsed data
      useAlbumModalStore.getState().open({ layout, editorRef, editingAlbum })
    } else {
      // Parse the element
      const albumEl = editingAlbum as HTMLElement | null
      if (albumEl) {
        const albumLayout = albumEl.classList.contains("ck-album-masonry")
          ? "masonry"
          : albumEl.classList.contains("ck-album-slider")
            ? "slider"
            : "grid"

        const images: Array<{ url: string; display?: string; thumbnail?: string; alt: string; name?: string }> = []
        const imgElements = albumEl.querySelectorAll("img.album-img")

        imgElements.forEach((img) => {
          const src = img.getAttribute("src") || ""
          const alt = img.getAttribute("alt") || ""
          images.push({ url: src, display: src, alt })
        })

        // albumEl là nội dung preview bên trong widget HTML Embed. Để cập nhật
        // đúng model rawHtml khi lưu, truyền bọc widget `.raw-html-embed` làm element.
        const widgetEl = (albumEl.closest(".raw-html-embed") as HTMLElement | null) ?? albumEl

        useAlbumModalStore.getState().open({
          layout: albumLayout,
          editorRef,
          editingAlbum: { element: widgetEl, images, layout: albumLayout },
        })
      } else {
        useAlbumModalStore.getState().open({ layout, editorRef })
      }
    }
  }) as EventListener)

  // Sửa album: DOUBLE-click để mở modal. Single-click để nguyên cho CKEditor
  // chọn widget HTML Embed (rồi Delete/Backspace xóa cả khối trong 1 nhịp).
  document.addEventListener("dblclick", (e) => {
    const target = e.target as HTMLElement

    // Bỏ qua nếu double-click nằm trong chính modal album
    const modal = document.querySelector(".fixed.inset-0.bg-black\\/50")
    if (modal?.contains(target)) return

    // Widget HTML Embed phủ overlay lên preview → target thường là chính bọc
    // widget, không phải `.ck-image-album` con. Dò từ bọc widget xuống.
    const widget = target.closest(".raw-html-embed") as HTMLElement | null
    const albumEl = (widget?.querySelector(".ck-image-album")
      ?? target.closest(".ck-image-album")) as HTMLElement | null
    if (!albumEl) return

    // Chặn CKEditor mở trình sửa source của HTML Embed
    e.preventDefault()
    e.stopPropagation()

    // Open album for editing
    window.dispatchEvent(new CustomEvent("ck-open-album-modal", {
      detail: { editingAlbum: albumEl },
    }))
  }, true)
}

// ─── Helper for consumers to register the editor ref ────────────────────────────

export function setAlbumEditorRef(ref: any) {
  if (typeof window !== "undefined") {
    ;(window as any).__ckeditorAlbumEditorRef = ref
  }
}
